/**
 * Dev-tools gamify trial-extension override.
 *
 * Same shape as the billing override (`./overrides.ts`): a sessionStorage-backed
 * atom holding one active state, applied inside the gamify query's `select`, so
 * the gift, its hover card, the checklist modal and the completion tooltip all
 * read the overridden progress. The cache keeps the real response, so clearing
 * the override restores it instantly and a refetch can't wipe it.
 *
 * Production safety: `applyGamifyDevOverride` is the identity whenever
 * `getEnvironment()` reports production, read per call.
 */
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import {
  ActionType,
  ExtensionTaskId,
  SubscriptionTier,
  TaskIcon,
  TaskType,
} from '@heidi/schema/ml-scribe';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { GamifyTrialExtensionResponse } from '../../contracts';

const isNonProductionEnvironment = (): boolean => getEnvironment() !== ENV_NAME.PROD;

type ExtensionTask = NonNullable<GamifyTrialExtensionResponse['tasks']>[number];

export type GamifyDevState = 'notStarted' | 'oneLeft' | 'earned' | 'notEligible';

export const GAMIFY_DEV_STATES: {
  id: GamifyDevState;
  label: string;
  description: string;
}[] = [
  {
    id: 'notStarted',
    label: 'Not started',
    description: 'Eligible, no task progress. Gift shows the unread dot.',
  },
  {
    id: 'oneLeft',
    label: 'One task left',
    description: 'Every task done except the last.',
  },
  {
    id: 'earned',
    label: 'Earned (extended)',
    description:
      'All tasks done and the trial extended. Earned modal, Upgrade button and completion tooltip.',
  },
  {
    id: 'notEligible',
    label: 'Not eligible',
    description: 'No checklist. Gift and every entry point hide.',
  },
];

const sessionStorageAdapter = createJSONStorage<GamifyDevState | null>(() => {
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };
  }
  return window.sessionStorage;
});

export const gamifyDevOverrideAtom = atomWithStorage<GamifyDevState | null>(
  'heidi.devTools.gamifyOverride',
  null,
  sessionStorageAdapter
);

/** The Heidi 2 Clinician checklist, used when the real response has no tasks. */
const HEIDI_2_CLINICIAN_TASKS: ExtensionTask[] = [
  {
    id: ExtensionTaskId.create_notes,
    title: 'Create 5 scribe session notes',
    icon: TaskIcon.audio_lines,
    task_type: TaskType.counter,
    current: 0,
    target: 5,
    completed: false,
    action_type: null,
    action_url: null,
  },
  {
    id: ExtensionTaskId.create_custom_template,
    title: 'Create a custom template',
    icon: TaskIcon.blocks,
    task_type: TaskType.boolean,
    current: 0,
    target: 1,
    completed: false,
    action_type: ActionType.route,
    action_url: '/templates',
  },
  {
    id: ExtensionTaskId.ask_heidi_tasks,
    title: 'Ask Heidi to do 5 tasks',
    icon: TaskIcon.message_square,
    task_type: TaskType.counter,
    current: 0,
    target: 5,
    completed: false,
    action_type: ActionType.route,
    action_url: '/scribe/chat/projects',
  },
];

const withProgress = (task: ExtensionTask, done: boolean): ExtensionTask => ({
  ...task,
  current: done ? task.target : 0,
  completed: done,
});

/**
 * Rebuild the response for the chosen state, keeping the real task list and
 * tier when the account has one so the preview matches its actual checklist.
 */
export function applyGamifyDevOverride(
  data: GamifyTrialExtensionResponse,
  state: GamifyDevState | null
): GamifyTrialExtensionResponse {
  if (!isNonProductionEnvironment() || !state) {
    return data;
  }
  if (state === 'notEligible') {
    return {
      ...data,
      eligible: false,
      tasks: [],
      all_tasks_complete: false,
      trial_extended: false,
    };
  }

  const tasks = data.tasks?.length ? data.tasks : HEIDI_2_CLINICIAN_TASKS;
  const lastIndex = tasks.length - 1;
  const earned = state === 'earned';

  return {
    ...data,
    tier: data.tier ?? SubscriptionTier.CLINICIAN_PRO,
    // The backend stops reporting `eligible` once the extension is applied.
    eligible: !earned,
    all_tasks_complete: earned,
    trial_extended: earned,
    tasks: tasks.map((task, index) =>
      withProgress(task, earned || (state === 'oneLeft' && index < lastIndex))
    ),
  };
}
